// Invoice PDF (Stage 2E-3): A4, vector text (jsPDF, Helvetica), same style as the quotation PDF (quotationPdf.js is not
// changed). Pure — no network, no database, no clock:
//   • issued / cancelled copies are drawn ONLY from the invoice's frozen snapshots (issuer_snapshot, client_snapshot), the
//     database's stored lines and totals, and the numbers of the documents it is linked to (quotation / replaced invoice),
//     which can't change after issue; images are the snapshot's logo/QR bytes handed in by the caller;
//   • a draft preview uses the current settings/client and the database's preview totals, and is marked DRAFT;
//   • the cancelled copy is a separate document with a CANCELLED marking (the stored original is never touched).
// Title is always "INVOICE" — never a tax/VAT invoice; no VAT field, rate or amount; only the owner's statement (D5/D8).
// The creation date and file id are fixed from the record, so the same input gives the same bytes.
import { jsPDF } from "jspdf";
import { amountInWords } from "./amountWords.js";
import { npr, qty, discountLabel, dualDate, ktmDate, unprintable } from "./format.js";
import { VAT_STATEMENT } from "./quotationPdf.js";
import { drawStamp } from "./stampPdf.js";

// D9 — fail closed: the standard font can't print some characters (Nepali script, arrows, emoji…) and jsPDF would silently
// print garbage. Refuse to build the PDF instead, naming the field (the editor already blocks Issue for such text).
export function assertPrintable({ invoice: v, lines, issuer = {}, client = {} }) {
    const bad = unprintable([
        ["Company name", issuer.company_name], ["Company address", issuer.address], ["Company phone", issuer.phone], ["Company email", issuer.email], ["Website", issuer.website],
        ["Bank", issuer.bank_name], ["Account name", issuer.bank_account_name], ["Account number", issuer.bank_account_no], ["Branch", issuer.bank_branch],
        ["Client name", client.name], ["Client contact person", client.contact_person], ["Client address", client.address], ["Client phone", client.phone], ["Client email", client.email],
        ["Subject", v.subject], ["Client reference", v.client_reference], ["Terms", v.terms], ["Notes", v.notes], ["Cancel reason", v.cancel_reason],
        ...(lines || []).flatMap((l, i) => [[`Line ${i + 1} description`, l.description], [`Line ${i + 1} unit`, l.unit]]),
    ]);
    if (bad) { const e = new Error(`The PDF can't be created: ${bad[0]} contains characters the PDF font can't print (for example Nepali script or symbols).`); e.code = "UNPRINTABLE"; throw e; }
}

const W = 210, H = 297, M = 15, CW = W - 2 * M, BOTTOM = 276;
const DARK = [17, 24, 39], MUTED = [96, 104, 118], RULE = [214, 218, 225], FILL = [241, 243, 246], RED = [200, 30, 30];
const COLS = [
    { key: "no", title: "#", w: 8 },
    { key: "description", title: "Description", w: 68 },
    { key: "qty", title: "Qty", w: 16, right: true },
    { key: "unit", title: "Unit", w: 16 },
    { key: "rate", title: "Rate", w: 24, right: true },
    { key: "disc", title: "Disc.", w: 20, right: true },
    { key: "amount", title: "Amount", w: 28, right: true },
];
function font(doc, size, style = "normal", color = DARK) { doc.setFont("helvetica", style); doc.setFontSize(size); doc.setTextColor(...color); }
const lh = (size) => size * 0.42;

/**
 * @param {object} p
 * @param {"draft"|"issued"|"cancelled"} p.mode
 * @param {object} p.invoice      invoice row (doc_no, invoice_date, due_date, service_from/to, totals, discounts, subject, client_reference, notes, terms, issued_at, cancelled_at, cancel_reason, id)
 * @param {object[]} p.lines      database lines, in order
 * @param {object} p.issuer       issuer snapshot (company_name, pan, address, phone, email, website, bank_*)
 * @param {object} p.client       client snapshot (name, contact_person, address, phone, email, pan)
 * @param {object} p.cal          makeCalendar() of verified months (BS dates)
 * @param {string|null} [p.quotationNo]   number of the quotation it was created from
 * @param {string|null} [p.replacesNo]    number of the cancelled invoice it replaces
 * @param {string|null} [p.replacedByNo]  (cancelled copy only) number of its replacement, when issued
 * @param {{data: Uint8Array|string, format: "PNG"|"JPEG", w: number, h: number}|null} [p.logo]
 * @param {{data: Uint8Array|string, format: "PNG"|"JPEG", w: number, h: number}|null} [p.qr]
 * @param {{data: Uint8Array|string, format: "PNG"|"JPEG", w: number, h: number}|null} [p.stamp]  company stamp (never drawn on a draft)
 * @returns {jsPDF}
 */
export function buildInvoicePdf({ mode, invoice: v, lines, issuer = {}, client = {}, cal, quotationNo = null, replacesNo = null, replacedByNo = null, logo = null, qr = null, stamp = null }) {
    if (!["draft", "issued", "cancelled"].includes(mode)) throw new Error("mode");
    if (mode !== "draft" && (!v.doc_no || !v.issued_at)) throw new Error("An issued copy needs an issued invoice");
    if (mode === "cancelled" && !v.cancelled_at) throw new Error("A cancelled copy needs a cancelled invoice");
    assertPrintable({ invoice: v, lines, issuer, client });
    const doc = new jsPDF({ unit: "mm", format: "a4", compress: false, putOnlyUsedFonts: true });
    doc.setCreationDate(new Date(mode === "cancelled" ? v.cancelled_at : mode === "issued" ? v.issued_at : v.updated_at || v.created_at || 0));
    doc.setFileId(String(v.id || "").replace(/-/g, "").toUpperCase().padEnd(32, "0").slice(0, 32));
    const company = issuer.company_name || "";
    doc.setDocumentProperties({ title: mode === "draft" ? "Invoice (draft)" : `Invoice ${v.doc_no}${mode === "cancelled" ? " (cancelled)" : ""}`,
        subject: "Invoice", author: company, creator: company });
    const docNo = mode === "draft" ? "DRAFT - not yet issued" : v.doc_no;
    let y = M;
    const ensure = (h) => { if (y + h > BOTTOM) { doc.addPage(); y = M; font(doc, 8, "normal", MUTED); doc.text(`INVOICE ${docNo} (continued)`, M, y + 3); y += 9; return true; } return false; };

    // ─── Header: company (left), title + number + dates (right) ─────────────
    let xL = M;
    if (logo) {
        const s = Math.min(30 / logo.w, 20 / logo.h), lw = logo.w * s, lhgt = logo.h * s;
        doc.addImage(logo.data, logo.format, M, M, lw, lhgt, "logo", "FAST");
        xL = M + lw + 5;
    }
    const leftW = 110 - (xL - M);
    let yl = M + 5;
    font(doc, 13, "bold"); for (const t of doc.splitTextToSize(company || "—", leftW)) { doc.text(t, xL, yl); yl += lh(13); }
    font(doc, 8.5, "normal", MUTED); yl += 0.5;
    const detail = [issuer.address, [issuer.phone, issuer.email].filter(Boolean).join(" · "), issuer.website, issuer.pan ? `PAN: ${issuer.pan}` : ""].filter(Boolean);
    for (const d of detail) for (const t of doc.splitTextToSize(d, leftW)) { doc.text(t, xL, yl); yl += lh(8.5); }
    if (logo) yl = Math.max(yl, M + 22);
    let yr = M + 6;
    font(doc, 20, "bold"); doc.text("INVOICE", W - M, yr, { align: "right" }); yr += 7;
    for (const [k, val] of [["No.", docNo], ["Date", dualDate(v.invoice_date, cal)], ["Due date", dualDate(v.due_date, cal)]]) {
        font(doc, 9, "bold"); const kw = doc.getTextWidth(`${k}: `);
        font(doc, 9, "normal"); const vw = doc.getTextWidth(val);
        font(doc, 9, "bold"); doc.text(`${k}: `, W - M - vw - kw, yr);
        font(doc, 9, "normal"); doc.text(val, W - M, yr, { align: "right" }); yr += lh(9) + 0.6;
    }
    y = Math.max(yl, yr) + 2;
    doc.setDrawColor(...RULE); doc.setLineWidth(0.4); doc.line(M, y, W - M, y); y += 6;

    if (mode === "cancelled") {
        const reason = v.cancel_reason ? ` Reason: ${String(v.cancel_reason).replace(/[.\s]+$/, "")}.` : "";
        const repl = replacedByNo ? ` Replaced by ${replacedByNo}.` : "";
        font(doc, 9, "bold", RED);                // measure in the font it is drawn in
        const msg = doc.splitTextToSize(`CANCELLED on ${dualDate(ktmDate(v.cancelled_at), cal)}.${reason}${repl} This copy is for reference only; the invoice is no longer valid.`, CW - 8);
        const h = msg.length * lh(9) + 5;
        doc.setDrawColor(...RED); doc.setLineWidth(0.5); doc.rect(M, y, CW, h);
        msg.forEach((t, i) => doc.text(t, M + 4, y + 5 + i * lh(9)));
        y += h + 5;
    }

    // ─── Bill to (left) and references (right) ──────────────────────────────
    const yTop = y;
    font(doc, 7.5, "bold", MUTED); doc.text("BILL TO", M, y); y += 4.5;
    font(doc, 10.5, "bold"); for (const t of doc.splitTextToSize(client.name || "—", 105)) { doc.text(t, M, y); y += lh(10.5); }
    font(doc, 8.5, "normal");
    const cl = [client.contact_person ? `Attn: ${client.contact_person}` : "", client.address, [client.phone, client.email].filter(Boolean).join(" · "),
        client.pan ? `PAN: ${client.pan}` : ""].filter(Boolean);
    for (const d of cl) for (const t of doc.splitTextToSize(d, 105)) { doc.text(t, M, y); y += lh(8.5); }
    const refs = [quotationNo ? ["Ref.", `Quotation ${quotationNo}`] : null, v.client_reference ? ["Client reference", v.client_reference] : null,
        v.service_from && v.service_to ? ["Service period", `${dualDate(v.service_from, cal)}\nto ${dualDate(v.service_to, cal)}`] : null,
        replacesNo ? ["Replaces", `${replacesNo} (cancelled)`] : null].filter(Boolean);
    let yref = yTop;
    for (const [k, val] of refs) {
        font(doc, 7.5, "bold", MUTED); doc.text(k.toUpperCase(), W - M, yref, { align: "right" }); yref += 3.8;
        font(doc, 8.5, "normal");
        for (const part of val.split("\n")) for (const t of doc.splitTextToSize(part, 70)) { doc.text(t, W - M, yref, { align: "right" }); yref += lh(8.5); }
        yref += 1.2;
    }
    y = Math.max(y, yref) + 2;
    if (v.subject) {
        font(doc, 9, "bold"); const sw = doc.getTextWidth("Subject: ");
        const st = (() => { font(doc, 9, "normal"); return doc.splitTextToSize(v.subject, CW - sw); })();
        font(doc, 9, "bold"); doc.text("Subject:", M, y);
        font(doc, 9, "normal"); st.forEach((t, i) => doc.text(t, M + sw, y + i * lh(9))); y += st.length * lh(9) + 2;
    }
    y += 2;

    // ─── Lines ──────────────────────────────────────────────────────────────
    const headerRow = () => {
        doc.setFillColor(...FILL); doc.rect(M, y, CW, 7, "F");
        font(doc, 8, "bold"); let x = M;
        for (const c of COLS) { doc.text(c.title, c.right ? x + c.w - 1.5 : x + 1.5, y + 4.7, c.right ? { align: "right" } : undefined); x += c.w; }
        y += 7;
    };
    ensure(16); headerRow();
    lines.forEach((l, i) => {
        font(doc, 8.5, "normal");
        const cells = { no: [String(i + 1)], description: doc.splitTextToSize(l.description || "", 68 - 3), qty: [qty(l.quantity)],
            unit: doc.splitTextToSize(l.unit || "", 16 - 3), rate: [npr(l.rate)], disc: [discountLabel(l.discount_type, l.discount_value)], amount: [npr(l.amount)] };
        const rowH = Math.max(cells.description.length, cells.unit.length) * lh(8.5) + 3.2;
        if (ensure(rowH)) headerRow();
        let x = M;
        font(doc, 8.5, "normal");
        for (const c of COLS) {
            const cx = c.right ? x + c.w - 1.5 : x + 1.5, top = y + 4.2, opt = c.right ? { align: "right" } : undefined;
            cells[c.key].forEach((t, j) => doc.text(t, cx, top + j * lh(8.5), opt));
            x += c.w;
        }
        y += rowH; doc.setDrawColor(...RULE); doc.setLineWidth(0.2); doc.line(M, y, W - M, y);
    });
    y += 4;

    // ─── Totals (kept together) ─────────────────────────────────────────────
    const rows = [["Subtotal", npr(v.subtotal)]];
    if (Number(v.line_discount_total) > 0) rows.push(["Line discounts", `-${npr(v.line_discount_total)}`]);
    if (Number(v.doc_discount_total) > 0) rows.push([v.doc_discount_type === "percent" ? `Discount (${qty(v.doc_discount_value)}%)` : "Discount", `-${npr(v.doc_discount_total)}`]);
    ensure(rows.length * 5.2 + 10);
    const xl = W - M - 85;
    for (const [k, val] of rows) { font(doc, 9, "normal", MUTED); doc.text(k, xl, y); font(doc, 9, "normal"); doc.text(val, W - M, y, { align: "right" }); y += 5.2; }
    doc.setDrawColor(...DARK); doc.setLineWidth(0.4); doc.line(xl, y - 2.4, W - M, y - 2.4); y += 2.2;
    font(doc, 10.5, "bold"); doc.text("TOTAL DUE (NPR)", xl, y); doc.text(npr(v.total), W - M, y, { align: "right" }); y += 8;

    // ─── Amount in words + VAT statement ────────────────────────────────────
    font(doc, 9, "bold"); const aw = doc.getTextWidth("Amount in words: ");
    font(doc, 9, "normal"); const words = doc.splitTextToSize(amountInWords(v.total), CW - aw);
    ensure(words.length * lh(9) + 9);
    font(doc, 9, "bold"); doc.text("Amount in words:", M, y);
    font(doc, 9, "normal"); words.forEach((t, i) => doc.text(t, M + aw, y + i * lh(9))); y += words.length * lh(9) + 2.5;
    font(doc, 8.5, "italic", MUTED); doc.text(VAT_STATEMENT, M, y); y += 8;

    // ─── Terms and notes ────────────────────────────────────────────────────
    const flow = (title, body) => {
        if (!body) return;
        font(doc, 8.5, "normal"); const t = doc.splitTextToSize(body, CW);
        ensure(5 + Math.min(t.length, 2) * lh(8.5));
        font(doc, 9, "bold"); doc.text(title, M, y); y += 4.6;
        font(doc, 8.5, "normal", DARK);
        for (const s of t) { if (ensure(lh(8.5))) font(doc, 8.5, "normal", DARK); doc.text(s, M, y); y += lh(8.5); }
        y += 4;
    };
    flow("Terms & conditions", v.terms);
    flow("Notes", v.notes);

    // ─── Payment details + QR (kept together) ───────────────────────────────
    const pay = [["Bank", issuer.bank_name], ["Account name", issuer.bank_account_name], ["Account number", issuer.bank_account_no], ["Branch", issuer.bank_branch]].filter(([, x]) => x);
    if (pay.length || qr) {
        const h = Math.max(12 + pay.length * 5, qr ? 38 : 0) + 4;
        ensure(h);
        doc.setDrawColor(...RULE); doc.setLineWidth(0.3); doc.rect(M, y, CW, h);
        font(doc, 9, "bold"); doc.text("Payment details", M + 4, y + 6);
        let yp = y + 11.5;
        for (const [k, val] of pay) { font(doc, 8.5, "normal", MUTED); doc.text(`${k}:`, M + 4, yp); font(doc, 8.5, "bold"); doc.text(doc.splitTextToSize(String(val), 95)[0], M + 32, yp); yp += 5; }
        if (mode !== "draft") { font(doc, 8, "italic", MUTED); doc.text(`Please quote ${v.doc_no} with your payment.`, M + 4, yp + 1); }
        if (qr) {
            const s = Math.min(30 / qr.w, 30 / qr.h), qw = qr.w * s, qh = qr.h * s;
            doc.addImage(qr.data, qr.format, W - M - 4 - qw, y + 3, qw, qh, "qr", "FAST");
            font(doc, 7, "normal", MUTED); doc.text("Scan to pay", W - M - 4 - qw / 2, y + 3 + qh + 3.5, { align: "center" });
        }
        y += h + 6;
    }

    // ─── Signature / stamp area ─────────────────────────────────────────────
    ensure(30);
    const xs = W - M - 70;
    y += 16;
    doc.setDrawColor(...DARK); doc.setLineWidth(0.3); doc.line(xs, y, W - M, y);
    drawStamp(doc, mode === "draft" ? null : stamp, xs, y);
    font(doc, 8.5, "bold"); for (const t of doc.splitTextToSize(`For ${company || "—"}`, 70)) { y += 4.4; doc.text(t, xs, y); }
    font(doc, 7.5, "normal", MUTED); y += 4; doc.text("Authorised signature & stamp", xs, y);

    // ─── Every page: marking (draft / cancelled) and footer with page numbers ──
    const n = doc.getNumberOfPages();
    for (let i = 1; i <= n; i++) {
        doc.setPage(i);
        if (mode !== "issued") {
            const word = mode === "draft" ? "DRAFT" : "CANCELLED", size = mode === "draft" ? 110 : 76, a = 35 * Math.PI / 180;
            doc.saveGraphicsState(); doc.setGState(new doc.GState({ opacity: 0.13 }));
            font(doc, size, "bold", mode === "draft" ? [120, 120, 120] : RED);
            const tw = doc.getTextWidth(word);
            doc.text(word, W / 2 - (tw / 2) * Math.cos(a), H / 2 + 10 + (tw / 2) * Math.sin(a), { angle: 35 });
            doc.restoreGraphicsState();
        }
        doc.setDrawColor(...RULE); doc.setLineWidth(0.3); doc.line(M, H - 13, W - M, H - 13);
        font(doc, 7.5, "normal", MUTED);
        doc.text(mode === "draft" ? "DRAFT - not a valid invoice" : `${v.doc_no} · Computer-generated invoice${mode === "cancelled" ? " · CANCELLED" : ""}`, M, H - 8.5);
        doc.text(`Page ${i} of ${n}`, W - M, H - 8.5, { align: "right" });
    }
    return doc;
}

export const invoicePdfBytes = (p) => new Uint8Array(buildInvoicePdf(p).output("arraybuffer"));
